/**
 * Reporting an incident for someone else.
 *
 * Any signed-in employee may raise an incident for a colleague — the same as
 * e-mailing IT about them. The colleague becomes the ticket's Customer (they
 * get Ivanti's updates, and it is in *their* My tickets, not the reporter's),
 * and the server writes who reported it at the top of the description.
 *
 * The default is always the signed-in person: nothing is sent, and nothing
 * extra is shown, unless somebody else was chosen — by the person, through
 * "Reporting this for someone else?", or by the assistant when they said so.
 *
 * Pure and import-free so `node --test` can load it straight from source.
 */

/** The colleague an incident is for. */
export interface OnBehalfPerson {
  email: string;
  displayName: string;
}

/** The parts of a directory hit this reads. */
export interface DirectoryPersonLike {
  display_name: string;
  email?: string | null;
}

/** The parts of a ticket's requester this reads. */
export interface RequesterLike {
  display_name?: string | null;
  email?: string | null;
}

function normalised(email: string | null | undefined): string {
  return (email ?? "").trim().toLowerCase();
}

/**
 * The person a directory pick stands for, or null when it cannot be filed for.
 *
 * The server takes an address, so a directory entry without one cannot be
 * chosen — better to say so at the picker than to fail at Submit.
 */
export function personFromDirectory(
  entry: DirectoryPersonLike | null | undefined,
): OnBehalfPerson | null {
  if (entry == null) {
    return null;
  }
  const email = (entry.email ?? "").trim();
  if (email === "" || !email.includes("@")) {
    return null;
  }
  return { email, displayName: entry.display_name.trim() || email };
}

/** True when `person` is somebody other than the signed-in user. */
export function isSomeoneElse(
  person: OnBehalfPerson | null | undefined,
  signedInEmail: string | null | undefined,
): boolean {
  if (person == null || normalised(person.email) === "") {
    return false;
  }
  return normalised(person.email) !== normalised(signedInEmail);
}

/**
 * The body field for an incident filed for `person`.
 *
 * Empty (the key omitted, not sent blank) when it is for the signed-in user,
 * so an ordinary report's body — and its idempotency fingerprint — is exactly
 * what it was before this existed.
 */
export function onBehalfField(
  person: OnBehalfPerson | null | undefined,
  signedInEmail: string | null | undefined,
): { on_behalf_of_email?: string } {
  return isSomeoneElse(person, signedInEmail) && person != null
    ? { on_behalf_of_email: person.email.trim() }
    : {};
}

/**
 * Whose name goes in "Raised for <name>".
 *
 * The created ticket's requester is the colleague, so its name is the
 * authoritative one; the picked name is the fallback when the ticket came
 * back without one.
 */
export function raisedForName(
  requester: RequesterLike | null | undefined,
  picked: OnBehalfPerson | null | undefined,
): string | null {
  const fromTicket = (requester?.display_name ?? "").trim();
  if (fromTicket !== "") {
    return fromTicket;
  }
  const fromPick = (picked?.displayName ?? "").trim();
  if (fromPick !== "") {
    return fromPick;
  }
  const email = (requester?.email ?? picked?.email ?? "").trim();
  return email === "" ? null : email;
}
